/* eslint-disable no-case-declarations */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import WindowQuery from '../utils/WindowQuery';
import formatNumber from '../utils/formatNumber';
import mls_property_types from '../constants/mls_property_types.json';
import mls_categories from '../constants/mls_categories.json';
import mls_statuses, { otherStatuses } from '../constants/mls_statuses';
import mls_amenities from '../constants/mls_amenities.json';
import mls_results_sorting from '../constants/mls_results_sorting.json';
import mls_listing_statuses from '../constants/mls_listing_statuses.json';

export const MLSMapContext = React.createContext();

// TODO: Pass Ad Block props

export default function MLSMapProvider({ areas, children, placards }) {
  const updateQuery = new WindowQuery();

  const mapRef = useRef();

  const [meta, setMeta] = useState();
  const [mapCenter, setMapCenter] = useState({ lat: 30.3935, lng: -86.4958 });
  const [mapBounds, setMapBounds] = useState();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [highlightedMarkerID, setHighlightedMarkerID] = useState(null);
  const [searchOnMove, setSearchOnMove] = useState(true);
  const [showSearchOnMove, setShowSearchOnMove] = useState(false);
  const [showRedoSearch, setShowRedoSearch] = useState(false);

  const [bedrooms, setBedrooms] = useState(0);
  const [bathrooms, setBathrooms] = useState(0);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minSqFt, setMinSqFt] = useState('');
  const [maxSqFt, setMaxSqFt] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [typeFilter, setTypeFilter] = useState(
    mls_property_types.find(type => type.value === 'A')
  );
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [statusFilters, setStatusFilters] = useState(
    mls_listing_statuses.filter(
      status => status.value === 'Active' || status.value === 'Coming Soon'
    )
  );
  const [minYearBuilt, setMinYearBuilt] = useState('');
  const [maxYearBuilt, setMaxYearBuilt] = useState('');
  const [minLotSize, setMinLotSize] = useState('');
  const [maxLotSize, setMaxLotSize] = useState('');
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [mapAreas, setMapAreas] = useState([]);
  const [zipcode, setZipcode] = useState('');

  const [showFilters, setShowFilters] = useState(false);
  const [sortDropdownVisible, setSortDropdownVisible] = useState(false);
  const [typeDropdownVisible, setTypeDropdownVisible] = useState(false);
  const [statusDropdownVisible, setStatusDropdownVisible] = useState(false);
  const [categoryDropdownVisible, setCategoryDropdownVisible] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const [listingURL, setListingURL] = useState(null);
  const [currentListing, setCurrentListing] = useState(null);
  const [addressContains, setAddressContains] = useState(null);

  useEffect(() => {
    if (window.google && (mapAreas.length > 0 || zipcode)) {
      const coder = new window.google.maps.Geocoder();
      const tempAreaBounds = new window.google.maps.LatLngBounds();

      const areasAndZipcode = [...mapAreas];
      if (zipcode) areasAndZipcode.push({ name: zipcode });

      const areaGeocodes = areasAndZipcode.map(area => {
        // For 30A West and 30A East the geocoded area coords end up centering the map on Panama City Beach and consequently the 30A listings
        // are no longer in view on the map. Therefore we will manually set coords for those two areas.
        if (area.name?.includes('30A')) {
          return new Promise((resolve, reject) =>
            resolve({
              results: [
                {
                  geometry: {
                    location: { lat: () => 30.318131, lng: () => -86.128853 },
                  },
                },
              ],
            })
          );
        }
        if (area.name?.includes('Navarre')) {
          return new Promise((resolve, reject) =>
            resolve({
              results: [
                {
                  geometry: {
                    location: { lat: () => 30.410182, lng: () => -86.912513 },
                  },
                },
              ],
            })
          );
        }
        if (area.name?.includes('Miramar')) {
          return new Promise((resolve, reject) =>
            resolve({
              results: [
                {
                  geometry: {
                    location: { lat: () => 30.373235, lng: () => -86.353766 },
                  },
                },
              ],
            })
          );
        }
        return coder.geocode({ address: `${area.name}, FL` });
      });

      Promise.all(areaGeocodes)
        .then(res => {
          const areaCoords = res.reduce(
            (acc, area) => {
              const areaLat = area.results[0].geometry.location.lat();
              const areaLng = area.results[0].geometry.location.lng();

              const loc = new window.google.maps.LatLng(areaLat, areaLng);
              tempAreaBounds.extend(loc);

              return {
                lat: acc.lat + area.results[0].geometry.location.lat(),
                lng: acc.lng + area.results[0].geometry.location.lng(),
              };
            },
            { lat: 0, lng: 0 }
          );

          setTimeout(() => {
            if (mapRef && mapRef.current) {
              mapRef.current.map_.fitBounds(tempAreaBounds);
              if (mapRef.current.map_.zoom > 12)
                mapRef.current.map_.setZoom(12);
            }
            setMapCenter({
              lat: areaCoords.lat / res.length,
              lng: areaCoords.lng / res.length,
            });
          }, 0);
        })
        .catch(err => {
          console.error(err);
          if (err) toast.error(err.message);
        });
    }
  }, [mapAreas, zipcode, mapLoaded]);

  // Grab query params on page load
  useEffect(() => {
    const queries = window.location.search.substr(1).split('&');
    const propertySlug = window.location.pathname.split('/')?.[2];

    if (propertySlug) {
      setListingURL(propertySlug);
    }
    queries.forEach(q => {
      const [queryName, queryValue] = q.split('=');

      switch (queryName) {
        case 'listing_url':
          setListingURL(queryValue);
          break;

        case 'address_contains':
          setAddressContains(decodeURI(queryValue));
          break;

        case 'type':
          setTypeFilter(
            mls_property_types.find(t => t.label === decodeURI(queryValue))
          );
          break;

        case 'categories':
          const queryValuesArray = decodeURI(queryValue).split(',');
          const urlType = window.location.search
            .substr(1)
            .split('&')
            .find(query => query.includes('type='));
          const decodedUrlType = urlType
            ? decodeURI(urlType.replace('type=', ''))
            : typeFilter.value;

          setCategoryFilter(
            mls_categories.filter(c => {
              if (urlType) {
                return (
                  c.type ===
                    mls_property_types.find(t => t.label === decodedUrlType)
                      .value && queryValuesArray.some(v => c.value === v)
                );
              }
              return (
                c.type === typeFilter.value &&
                queryValuesArray.some(v => c.value === v)
              );
            })
          );
          break;

        case 'areas':
          const areaStringArray = queryValue.toLowerCase().split(/,|%2C/);
          const areasArray = areas.filter(area =>
            areaStringArray.includes(area.slug)
          );
          updateQuery.remove('zipcode');

          setMapAreas(areasArray);
          break;

        case 'listing_statuses':
          const listingStatusesStringArray = decodeURI(
            queryValue.toLowerCase()
          ).split(/,|%2C/);
          const listingStatusesArray = mls_listing_statuses.filter(status =>
            listingStatusesStringArray.includes(
              decodeURI(status.value).toLowerCase()
            )
          );

          setStatusFilters(listingStatusesArray);
          break;

        case 'zipcode':
          setZipcode(queryValue);
          break;

        case 'bedrooms':
          setBedrooms(queryValue);
          break;

        case 'bathrooms':
          setBathrooms(queryValue);
          break;

        case 'minPrice':
          setMinPrice(formatNumber(parseInt(queryValue)));
          break;

        case 'maxPrice':
          setMaxPrice(formatNumber(parseInt(queryValue)));
          break;

        case 'minSqFt':
          setMinSqFt(formatNumber(parseInt(queryValue)));
          break;

        case 'maxSqFt':
          setMaxSqFt(formatNumber(parseInt(queryValue)));
          break;

        case 'minYearBuilt':
          setMinYearBuilt(formatNumber(parseInt(queryValue)));
          break;

        case 'maxYearBuilt':
          setMaxYearBuilt(formatNumber(parseInt(queryValue)));
          break;

        case 'minLotSize':
          setMinLotSize(formatNumber(parseInt(queryValue)));
          break;

        case 'maxLotSize':
          setMaxLotSize(formatNumber(parseInt(queryValue)));
          break;

        case 'filter_statuses':
          const statusValuesArray = decodeURI(queryValue).split(',');
          const mlsStatusesArray = [...mls_statuses, ...otherStatuses];

          setFilterStatuses(
            mlsStatusesArray.filter(s =>
              statusValuesArray.some(v => s.value === v)
            )
          );
          break;

        case 'amenities':
          const amenityValuesArray = decodeURI(queryValue).split(',');

          setAmenities(
            mls_amenities.filter(a =>
              amenityValuesArray.some(v => a.value === v)
            )
          );
          break;

        case 'sortBy':
          setSortBy(
            mls_results_sorting.find(s => s.value === decodeURI(queryValue))
          );
          break;

        default:
          console.error(`No case for query ${queryName} found!`);
          break;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update url to show filter values
  useEffect(() => {
    if (listingURL) updateQuery.add('listing_url', listingURL);
    else if (!listingURL) updateQuery.remove('listing_url');

    if (typeFilter) updateQuery.add('type', encodeURI(typeFilter.label));
    else if (!typeFilter) updateQuery.remove('type');

    if (categoryFilter.length > 0)
      updateQuery.add(
        'categories',
        categoryFilter.map(c => encodeURI(c.value)).join(',')
      );
    else if (categoryFilter.length === 0) updateQuery.remove('categories');

    if (statusFilters.length > 0) {
      updateQuery.add(
        'listing_statuses',
        statusFilters.map(s => encodeURI(s.value)).join(',')
      );
    } else if (statusFilters.length === 0)
      updateQuery.remove('listing_statuses');

    if (mapAreas.length > 0) {
      updateQuery.add('areas', mapAreas.map(a => encodeURI(a.slug)).join(','));
      updateQuery.remove('zipcode');
      setZipcode('');
    } else if (mapAreas.length === 0) updateQuery.remove('areas');

    if (bedrooms) updateQuery.add('bedrooms', bedrooms);
    else if (!bedrooms) updateQuery.remove('bedrooms');

    if (bathrooms) updateQuery.add('bathrooms', bathrooms);
    else if (!bathrooms) updateQuery.remove('bathrooms');

    if (minPrice) updateQuery.add('minPrice', minPrice.replace(/,/g, ''));
    else if (!minPrice) updateQuery.remove('minPrice');

    if (maxPrice) updateQuery.add('maxPrice', maxPrice.replace(/,/g, ''));
    else if (!maxPrice) updateQuery.remove('maxPrice');

    if (minSqFt) updateQuery.add('minSqFt', minSqFt.replace(/,/g, ''));
    else if (!minSqFt) updateQuery.remove('minSqFt');

    if (maxSqFt) updateQuery.add('maxSqFt', maxSqFt.replace(/,/g, ''));
    else if (!maxSqFt) updateQuery.remove('maxSqFt');

    if (minYearBuilt)
      updateQuery.add('minYearBuilt', minYearBuilt.replace(/,/g, ''));
    else if (!minYearBuilt) updateQuery.remove('minYearBuilt');

    if (maxYearBuilt)
      updateQuery.add('maxYearBuilt', maxYearBuilt.replace(/,/g, ''));
    else if (!maxYearBuilt) updateQuery.remove('maxYearBuilt');

    if (minLotSize) updateQuery.add('minLotSize', minLotSize.replace(/,/g, ''));
    else if (!minLotSize) updateQuery.remove('minLotSize');

    if (maxLotSize) updateQuery.add('maxLotSize', maxLotSize.replace(/,/g, ''));
    else if (!maxLotSize) updateQuery.remove('maxLotSize');

    if (filterStatuses.length > 0)
      updateQuery.add(
        'filter_statuses',
        filterStatuses.map(s => encodeURI(s.value)).join(',')
      );
    else if (filterStatuses.length === 0) updateQuery.remove('filter_statuses');

    if (amenities.length > 0)
      updateQuery.add(
        'amenities',
        amenities.map(a => encodeURI(a.value)).join(',')
      );
    else if (amenities.length === 0) updateQuery.remove('amenities');

    if (sortBy) updateQuery.add('sortBy', sortBy.value);
    else if (!sortBy) updateQuery.remove('sortBy');
    // Keep react-hooks/exhaustive-deps disabled.
    // It adds `updateQuery` to the dependencies which causes bugs when switch map page views
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bedrooms,
    bathrooms,
    minPrice,
    maxPrice,
    minSqFt,
    maxSqFt,
    typeFilter,
    categoryFilter,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    filterStatuses,
    amenities,
    sortBy,
    mapAreas,
    statusFilters,
    listingURL,
  ]);

  const clearFilters = () => {
    setBedrooms(0);
    setBathrooms(0);
    setMinPrice('');
    setMaxPrice('');
    setMinSqFt('');
    setMaxSqFt('');
    setMinYearBuilt('');
    setMaxYearBuilt('');
    setMinLotSize('');
    setMaxLotSize('');
    setSortBy('');
    setTypeFilter('');
    setStatusFilters([]);
    setCategoryFilter([]);
    setFilterStatuses([]);
    setAmenities([]);
    setMapAreas([]);
    setAddressContains('');
  };

  return (
    <MLSMapContext.Provider
      value={{
        placards,
        clearFilters,
        meta,
        setMeta,
        mapCenter,
        setMapCenter,
        mapBounds,
        setMapBounds,
        showFilters,
        setShowFilters,
        bedrooms,
        setBedrooms,
        bathrooms,
        setBathrooms,
        minPrice,
        setMinPrice,
        maxPrice,
        setMaxPrice,
        minSqFt,
        setMinSqFt,
        maxSqFt,
        setMaxSqFt,
        activeMarker,
        setActiveMarker,
        highlightedMarkerID,
        setHighlightedMarkerID,
        sortDropdownVisible,
        setSortDropdownVisible,
        sortBy,
        setSortBy,
        typeDropdownVisible,
        setTypeDropdownVisible,
        typeFilter,
        setTypeFilter,
        categoryFilter,
        setCategoryFilter,
        statusFilters,
        setStatusFilters,
        statusDropdownVisible,
        setStatusDropdownVisible,
        categoryDropdownVisible,
        setCategoryDropdownVisible,
        mapVisible,
        setMapVisible,
        searchOnMove,
        setSearchOnMove,
        showSearchOnMove,
        setShowSearchOnMove,
        showRedoSearch,
        setShowRedoSearch,
        minYearBuilt,
        setMinYearBuilt,
        maxYearBuilt,
        setMaxYearBuilt,
        minLotSize,
        setMinLotSize,
        maxLotSize,
        setMaxLotSize,
        filterStatuses,
        setFilterStatuses,
        amenities,
        setAmenities,
        mapAreas,
        setMapAreas,
        setMapLoaded,
        mapRef,
        zipcode,
        setZipcode,
        listingURL,
        setListingURL,
        addressContains,
        setAddressContains,
        currentListing,
        setCurrentListing,
      }}
    >
      {children}
    </MLSMapContext.Provider>
  );
}
